import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { financeDashboardAPI, approvalSLAsAPI } from '../api/client';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area,
  ComposedChart, ReferenceLine,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, FileText, Receipt, BarChart3,
  AlertTriangle, Clock, Building2, Users, Wallet, PiggyBank,
  Filter, RefreshCw, ChevronDown, ArrowUpRight, ArrowDownRight,
  Briefcase, Layers, UserCheck, ExternalLink, Shield,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';

// ──────────────────────────────────────────────
// COLOUR PALETTE
// ──────────────────────────────────────────────
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6'];
const STATUS_COLORS = { Draft: '#9ca3af', Pending: '#f59e0b', 'Pending Approval': '#f59e0b', Approved: '#10b981', Posted: '#3b82f6', Rejected: '#ef4444' };
const AGING_COLORS = { 'Not Due': '#10b981', '1-30 Days': '#f59e0b', '31-60 Days': '#f97316', '61-90 Days': '#ef4444', '90+ Days': '#991b1b' };

// ──────────────────────────────────────────────
// HELPERS
// ──────────────────────────────────────────────
const fmt = (v, currency = 'GBP') => {
  const n = parseFloat(v) || 0;
  const sym = { GBP: '£', USD: '$', EUR: '€' }[currency] || currency + ' ';
  return sym + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtShort = (v, currency = 'GBP') => {
  const n = parseFloat(v) || 0;
  const sym = { GBP: '£', USD: '$', EUR: '€' }[currency] || currency + ' ';
  if (n >= 1_000_000) return sym + (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return sym + (n / 1_000).toFixed(1) + 'K';
  return sym + n.toFixed(2);
};

const pct = (v) => (parseFloat(v) || 0).toFixed(1) + '%';

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[parseInt(mo) - 1] + ' ' + y.slice(2);
};

// ──────────────────────────────────────────────
// KPI CARD COMPONENT
// ──────────────────────────────────────────────
function KPICard({ title, value, subtitle, icon: Icon, color = 'blue', trend, trendLabel, status, progress, onClick }) {
  const clickable = !!onClick;
  const borderColor = {
    blue: 'border-blue-500', green: 'border-emerald-500', amber: 'border-amber-500',
    red: 'border-red-500', purple: 'border-violet-500', cyan: 'border-cyan-500',
  };
  const iconColor = {
    blue: 'text-blue-500', green: 'text-emerald-500', amber: 'text-amber-500',
    red: 'text-red-500', purple: 'text-violet-500', cyan: 'text-cyan-500',
  };
  const dotColor = {
    green: 'bg-emerald-400', amber: 'bg-amber-400', red: 'bg-red-400',
  };
  const progBar = {
    green: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500', blue: 'bg-blue-500', purple: 'bg-violet-500', cyan: 'bg-cyan-500',
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg shadow p-4 border-l-4 ${borderColor[color] || 'border-blue-500'} transition-all duration-150
        ${clickable ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:ring-2 hover:ring-offset-1 hover:ring-blue-200 active:scale-[0.98]' : 'hover:shadow-md'}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-4 h-4 flex-shrink-0 ${iconColor[color] || 'text-gray-400'}`} />
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">{title}</p>
        </div>
        <div className="flex items-center gap-1.5">
          {status && <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor[status] || 'bg-gray-300'}`} title={status === 'green' ? 'Good' : status === 'amber' ? 'Warning' : 'Critical'} />}
          {clickable && <ExternalLink className="w-3 h-3 text-gray-300 flex-shrink-0" />}
        </div>
      </div>
      <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
      {progress !== undefined && (
        <div className="mt-2">
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div className={`h-1.5 rounded-full ${progBar[color] || 'bg-blue-500'}`} style={{ width: `${Math.min(progress, 100)}%` }} />
          </div>
        </div>
      )}
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-1.5 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend).toFixed(1)}% {trendLabel || ''}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────
// MINI TABLE
// ──────────────────────────────────────────────
function MiniTable({ title, columns, rows, maxRows = 10 }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {columns.map((c, i) => (
                <th key={i} className={`px-3 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.slice(0, maxRows).map((row, ri) => (
              <tr key={ri} className="hover:bg-gray-50">
                {columns.map((c, ci) => (
                  <td key={ci} className={`px-3 py-2 ${c.align === 'right' ? 'text-right font-medium' : ''} ${ci === 0 ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} className="px-4 py-6 text-center text-gray-400">No data available</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// CHART CARD WRAPPER
// ──────────────────────────────────────────────
function ChartCard({ title, subtitle, children, className = '' }) {
  return (
    <div className={`bg-white rounded-lg border border-gray-200 overflow-hidden ${className}`}>
      <div className="px-4 py-2.5 border-b border-gray-100">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
        {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
      </div>
      <div className="p-3">
        {children}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// PROGRESS BAR
// ──────────────────────────────────────────────
function ProgressBar({ value, max, color = 'blue', showLabel = false }) {
  const pctVal = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const barColor = pctVal >= 90 ? 'bg-red-500' : pctVal >= 75 ? 'bg-amber-500' : 'bg-blue-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 bg-gray-100 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full transition-all ${barColor}`} style={{ width: `${pctVal}%` }} />
      </div>
      {showLabel && <span className="text-xs text-gray-500 w-10 text-right">{pctVal.toFixed(0)}%</span>}
    </div>
  );
}


// ──────────────────────────────────────────────
// FY CHART HELPERS
// ──────────────────────────────────────────────
function FYMonthTick({ x, y, payload, currentMonth }) {
  const isCurrent = payload.value === currentMonth;
  return (
    <text
      x={x} y={y + 12}
      textAnchor="middle"
      fill={isCurrent ? '#3b82f6' : '#6b7280'}
      fontSize={10}
      fontWeight={isCurrent ? 700 : 400}
    >
      {monthLabel(payload.value)}
    </text>
  );
}

function SpendTooltip({ active, payload, label, currency }) {
  if (!active || !payload?.length) return null;
  const inv = payload.find(p => p.dataKey === 'invoiceTotal');
  const exp = payload.find(p => p.dataKey === 'expenseTotal');
  const total = (inv?.value || 0) + (exp?.value || 0);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800 mb-1.5 border-b border-gray-100 pb-1">{monthLabel(label)}</p>
      {inv && <p className="flex justify-between gap-4"><span className="text-blue-500">● Invoices</span><span className="font-medium">{fmtShort(inv.value, currency)}</span></p>}
      {exp && <p className="flex justify-between gap-4"><span className="text-violet-500">● Expenses</span><span className="font-medium">{fmtShort(exp.value, currency)}</span></p>}
      {total > 0 && <p className="flex justify-between gap-4 border-t border-gray-100 mt-1 pt-1"><span className="text-gray-500 font-medium">Total</span><span className="font-bold text-gray-900">{fmtShort(total, currency)}</span></p>}
    </div>
  );
}

function VolumeTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const inv = payload.find(p => p.dataKey === 'invoiceCount');
  const exp = payload.find(p => p.dataKey === 'expenseCount');
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-800 mb-1.5 border-b border-gray-100 pb-1">{monthLabel(label)}</p>
      {inv && <p className="flex justify-between gap-4"><span className="text-blue-500">● Invoices</span><span className="font-medium">{inv.value}</span></p>}
      {exp && <p className="flex justify-between gap-4"><span className="text-violet-500">● Expenses</span><span className="font-medium">{exp.value}</span></p>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: OVERVIEW
// ══════════════════════════════════════════════════════════
function OverviewTab({ overview, trends, currency, filters, selectedFY, fyYear, navigate }) {
  // Build month slots for the full selected FY so the X-axis is always complete
  const fyMonths = useMemo(() => {
    const fy = (filters?.financialYears || []).find(f => String(f.FinancialYearID) === String(selectedFY));
    if (fy?.StartDate && fy?.EndDate) {
      const months = [];
      const cur = new Date(fy.StartDate);
      cur.setDate(1);
      const end = new Date(fy.EndDate);
      end.setDate(1);
      while (cur <= end) {
        months.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
        cur.setMonth(cur.getMonth() + 1);
      }
      return months;
    }
    // Fallback: last 12 months
    const months = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    return months;
  }, [filters, selectedFY]);

  const currentMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const paddedTrends = useMemo(() => {
    const byMonth = {};
    (trends || []).forEach(t => { byMonth[t.month] = t; });
    return fyMonths.map(m => byMonth[m] || { month: m, invoiceTotal: 0, expenseTotal: 0, invoiceCount: 0, expenseCount: 0 });
  }, [trends, fyMonths]);

  if (!overview) return <LoadingSkeleton />;

  const inv = overview.invoices || {};
  const exp = overview.expenses || {};
  const bud = overview.budget || {};
  const od  = overview.overdue || {};
  const sub = overview.subscriptions || {};

  const budgetUtil = bud.TotalBudget > 0
    ? ((parseFloat(bud.TotalSpent) + parseFloat(bud.TotalCommitted)) / parseFloat(bud.TotalBudget) * 100)
    : 0;
  const budgetStatus = budgetUtil >= 90 ? 'red' : budgetUtil >= 75 ? 'amber' : 'green';
  const overdueStatus = (od.OverdueCount || 0) > 5 ? 'red' : (od.OverdueCount || 0) > 0 ? 'amber' : 'green';

  const nav = (to, state) => navigate(to, { state: { from: '/finance-dashboard', fromLabel: 'Finance Dashboard', ...(fyYear ? { fyYear } : {}), ...state } });

  return (
    <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KPICard title="Total Invoices" value={inv.TotalInvoices || 0} subtitle={fmt(inv.TotalValue, currency)} icon={FileText} color="blue" status="green" onClick={() => nav('/invoices')} />
        <KPICard title="Outstanding" value={fmt(inv.OutstandingPayables, currency)} subtitle={`${(inv.PendingCount || 0) + (inv.ApprovedCount || 0)} invoices`} icon={Clock} color="amber" status={(inv.PendingCount || 0) + (inv.ApprovedCount || 0) > 10 ? 'amber' : 'green'} onClick={() => nav('/invoices', { filterStatus: 'Pending' })} />
        <KPICard title="Total Expenses" value={fmt(exp.TotalExpenseValue, currency)} subtitle={`${exp.TotalExpenses || 0} claims`} icon={Receipt} color="purple" onClick={() => nav('/expenses')} />
        <KPICard title="Budget Used" value={pct(budgetUtil)} subtitle={`${fmt(bud.TotalSpent, currency)} / ${fmt(bud.TotalBudget, currency)}`} icon={BarChart3} color={budgetStatus === 'red' ? 'red' : budgetStatus === 'amber' ? 'amber' : 'green'} status={budgetStatus} progress={budgetUtil} onClick={() => nav('/manage-budget')} />
        <KPICard title="Overdue" value={od.OverdueCount || 0} subtitle={fmt(od.OverdueValue, currency)} icon={AlertTriangle} color={overdueStatus === 'green' ? 'green' : 'red'} status={overdueStatus} onClick={() => nav('/invoices', { overdueFilter: true })} />
        <KPICard title="Subscriptions" value={sub.ActiveSubscriptions || 0} subtitle={`${fmt(sub.AnnualCommitment, currency)}/yr`} icon={RefreshCw} color="cyan" onClick={() => nav('/subscriptions')} />
      </div>

      {/* KPI Row 2 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard title="Posted" value={fmt(inv.PostedValue, currency)} subtitle={`${inv.PostedCount || 0} invoices`} icon={Wallet} color="green" status="green" onClick={() => nav('/invoices', { postedStatusFilter: 'Posted' })} />
        <KPICard title="Total VAT" value={fmt(inv.TotalVAT, currency)} icon={PiggyBank} color="blue" onClick={() => nav('/vat-claims')} />
        <KPICard title="Avg Invoice" value={fmt(inv.AvgInvoiceValue, currency)} subtitle={`${inv.UniqueVendors || 0} vendors`} icon={TrendingUp} color="purple" onClick={() => nav('/reports', { reportType: 'invoices' })} />
        <KPICard title="Due in 30 Days" value={fmt(sub.Next30DaysPayments, currency)} subtitle="Subscription payments" icon={DollarSign} color="amber" status={parseFloat(sub.Next30DaysPayments) > 0 ? 'amber' : 'green'} onClick={() => nav('/subscriptions')} />
      </div>

      {/* KPI Row 3: Payment Status */}
      {(inv.PaidCount > 0 || inv.UnpaidPostedCount > 0 || inv.PartiallyPaidCount > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPICard title="Paid (ERP)" value={fmt(inv.PaidValue, currency)} subtitle={`${inv.PaidCount || 0} invoices`} icon={Wallet} color="green" status="green" onClick={() => nav('/invoices', { erpStatusFilter: 'Paid' })} />
          <KPICard title="Partially Paid" value={inv.PartiallyPaidCount || 0} subtitle="invoices partially settled" icon={TrendingDown} color="amber" status={inv.PartiallyPaidCount > 0 ? 'amber' : 'green'} onClick={() => nav('/invoices', { erpStatusFilter: 'PartiallyPaid' })} />
          <KPICard title="Awaiting Payment" value={fmt(inv.UnpaidPostedValue, currency)} subtitle={`${inv.UnpaidPostedCount || 0} posted, unpaid`} icon={Clock} color="red" status={inv.UnpaidPostedCount > 0 ? 'amber' : 'green'} onClick={() => nav('/invoices', { postedStatusFilter: 'Posted', erpStatusFilter: 'Open' })} />
          <KPICard title="Payment Rate" value={inv.PostedCount > 0 ? pct((inv.PaidCount || 0) / inv.PostedCount * 100) : '—'} subtitle="of posted invoices paid" icon={TrendingUp} color="blue" onClick={() => nav('/invoice-posting')} />
        </div>
      )}

      {/* Budget Progress — single-line bar */}
      {bud.TotalBudget > 0 && (
        <div onClick={() => nav('/manage-budget')} className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 flex items-center gap-4 cursor-pointer hover:shadow-md hover:border-blue-300 transition-all duration-150">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Budget</span>
          <div className="flex-1 bg-gray-100 rounded-full h-2 relative">
            <div className={`h-2 rounded-full ${budgetUtil >= 90 ? 'bg-red-500' : budgetUtil >= 75 ? 'bg-amber-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(budgetUtil, 100)}%` }} />
          </div>
          <div className="flex items-center gap-3 text-xs whitespace-nowrap">
            <span className={`w-2 h-2 rounded-full ${budgetStatus === 'green' ? 'bg-emerald-400' : budgetStatus === 'amber' ? 'bg-amber-400' : 'bg-red-400'}`} />
            <span className="font-semibold text-gray-900">{pct(budgetUtil)}</span>
            <span className="text-gray-400">{fmt(bud.TotalSpent, currency)} / {fmt(bud.TotalBudget, currency)}</span>
            <span className="text-emerald-600 font-medium">{fmt(bud.TotalRemaining, currency)} left</span>
          </div>
        </div>
      )}

      {/* Trends Charts — full FY month slots on X-axis */}
      {paddedTrends.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Monthly Spend" subtitle="Full financial year — bars = spend, line = invoice trend">
            <ResponsiveContainer width="100%" height={270}>
              <ComposedChart data={paddedTrends} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradInv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={1} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.7} />
                  </linearGradient>
                  <linearGradient id="gradExp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                    <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={<FYMonthTick currentMonth={currentMonth} />}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  interval={0}
                />
                <YAxis
                  tickFormatter={(v) => fmtShort(v, currency)}
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  width={58}
                />
                <Tooltip content={<SpendTooltip currency={currency} />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  iconType="circle"
                  iconSize={7}
                />
                <ReferenceLine
                  x={currentMonth}
                  stroke="#3b82f6"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  label={{ value: 'Now', position: 'insideTopLeft', fontSize: 9, fill: '#3b82f6', dy: -6 }}
                />
                <Bar dataKey="invoiceTotal" name="Invoices" fill="url(#gradInv)" radius={[4,4,0,0]} barSize={14} minPointSize={2} />
                <Bar dataKey="expenseTotal" name="Expenses" fill="url(#gradExp)" radius={[4,4,0,0]} barSize={14} minPointSize={2} />
                <Line
                  type="monotone"
                  dataKey="invoiceTotal"
                  stroke="#1d4ed8"
                  strokeWidth={1.5}
                  dot={false}
                  strokeDasharray="5 3"
                  legendType="none"
                  name="_trend"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title="Monthly Volume" subtitle="Full financial year — number of invoices & expense claims">
            <ResponsiveContainer width="100%" height={270}>
              <BarChart data={paddedTrends} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={<FYMonthTick currentMonth={currentMonth} />}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#9ca3af' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<VolumeTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  iconType="circle"
                  iconSize={7}
                />
                <ReferenceLine
                  x={currentMonth}
                  stroke="#3b82f6"
                  strokeDasharray="4 3"
                  strokeWidth={1.5}
                  label={{ value: 'Now', position: 'insideTopLeft', fontSize: 9, fill: '#3b82f6', dy: -6 }}
                />
                <Bar dataKey="invoiceCount" name="Invoices" fill="#3b82f6" radius={[6,6,0,0]} barSize={14} minPointSize={2} />
                <Bar dataKey="expenseCount" name="Expenses" fill="#8b5cf6" radius={[6,6,0,0]} barSize={14} minPointSize={2} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Invoice Status — compact row */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Invoice Status</h3>
        <div className="grid grid-cols-5 gap-2">
          {[
            { label: 'Draft', count: inv.DraftCount, dot: 'bg-gray-400', status: 'Draft' },
            { label: 'Pending', count: inv.PendingCount, dot: 'bg-amber-400', status: 'Pending' },
            { label: 'Approved', count: inv.ApprovedCount, dot: 'bg-emerald-400', status: 'Approved' },
            { label: 'Posted', count: inv.PostedCount, dot: 'bg-blue-400', status: 'Posted' },
            { label: 'Rejected', count: inv.RejectedCount, dot: 'bg-red-400', status: 'Rejected' },
          ].map(s => (
            <div
              key={s.label}
              onClick={() => nav('/invoices', { filterStatus: s.status })}
              className="flex items-center gap-2 bg-gray-50 rounded-md px-3 py-2 cursor-pointer hover:bg-blue-50 hover:shadow-sm hover:-translate-y-0.5 transition-all duration-150 group"
            >
              <span className={`w-2 h-2 rounded-full ${s.dot}`} />
              <div className="min-w-0">
                <p className="text-lg font-bold text-gray-900 leading-tight">{s.count || 0}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">{s.label}</p>
              </div>
              <ExternalLink className="w-3 h-3 text-gray-300 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          ))}
        </div>
        {/* Payment status row — only shown when ERP sync data exists */}
        {(inv.PaidCount > 0 || inv.UnpaidPostedCount > 0 || inv.PartiallyPaidCount > 0) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">ERP Payment Status (Posted Invoices)</p>
            <div className="flex gap-4 flex-wrap">
              {[
                { label: 'Paid', count: inv.PaidCount, value: inv.PaidValue, dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', erpStatus: 'Paid' },
                { label: 'Partial', count: inv.PartiallyPaidCount, dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', erpStatus: 'PartiallyPaid' },
                { label: 'Awaiting Payment', count: inv.UnpaidPostedCount, value: inv.UnpaidPostedValue, dot: 'bg-red-400', text: 'text-red-700', bg: 'bg-red-50', erpStatus: 'Open' },
              ].map(s => (
                <div
                  key={s.label}
                  onClick={() => nav('/invoices', { erpStatusFilter: s.erpStatus, postedStatusFilter: 'Posted' })}
                  className={`flex items-center gap-2 ${s.bg} rounded-md px-3 py-1.5 cursor-pointer hover:shadow-sm hover:-translate-y-0.5 transition-all duration-150`}
                >
                  <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                  <div>
                    <span className={`text-xs font-semibold ${s.text}`}>{s.count || 0} {s.label}</span>
                    {s.value != null && <span className="text-[10px] text-gray-500 ml-1.5">{fmt(s.value, currency)}</span>}
                  </div>
                  <ExternalLink className="w-3 h-3 text-gray-400 ml-1" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: INVOICES & PAYABLES
// ══════════════════════════════════════════════════════════
function InvoicesTab({ data, currency, fyYear, navigate }) {
  if (!data) return <LoadingSkeleton />;
  const nav = (to, state) => navigate(to, { state: { from: '/finance-dashboard', fromLabel: 'Finance Dashboard', ...(fyYear ? { fyYear } : {}), ...state } });

  const agingOrder = ['Not Due', '1-30 Days', '31-60 Days', '61-90 Days', '90+ Days'];
  const sortedAging = agingOrder.map(b => data.aging?.find(a => a.AgingBucket === b) || { AgingBucket: b, Count: 0, TotalValue: 0 });

  return (
    <div className="space-y-4">
      {/* Status & Aging */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Invoice Status Distribution" subtitle="By value">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={data.byStatus || []}
                dataKey="TotalValue"
                nameKey="Status"
                cx="50%" cy="50%"
                outerRadius={65}
                innerRadius={30}
                paddingAngle={2}
                label={({ Status, percent }) => `${Status} ${(percent * 100).toFixed(0)}%`}
              >
                {(data.byStatus || []).map((entry, i) => (
                  <Cell key={entry.Status} fill={STATUS_COLORS[entry.Status] || COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Invoice Aging" subtitle="Unposted invoices by overdue period">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={sortedAging} layout="vertical" barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 10 }} />
              <YAxis dataKey="AgingBucket" type="category" width={70} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Bar dataKey="TotalValue" name="Value" radius={[0,5,5,0]}>
                {sortedAging.map((entry, i) => (
                  <Cell key={i} fill={AGING_COLORS[entry.AgingBucket] || COLORS[i]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Top Vendors & Department Spend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top 10 Vendors by Spend">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.topVendors || []} layout="vertical" barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 10 }} />
              <YAxis dataKey="VendorName" type="category" width={90} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Bar dataKey="TotalSpend" name="Spend" fill="#3b82f6" radius={[0,5,5,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Spend by Department">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data.byDepartment || []}
                dataKey="TotalValue"
                nameKey="DepartmentName"
                cx="50%" cy="50%"
                outerRadius={70}
                innerRadius={35}
                paddingAngle={2}
                label={({ DepartmentName, percent }) => `${(DepartmentName || '').substring(0,12)} ${(percent * 100).toFixed(0)}%`}
              >
                {(data.byDepartment || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Currency Split & GL Accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniTable
          title="Currency Split"
          columns={[
            { label: 'Currency', key: 'Currency' },
            { label: 'Count', key: 'Count', align: 'right' },
            { label: 'Original Total', align: 'right', render: (r) => fmt(r.OriginalTotal, r.Currency) },
            { label: 'Converted', align: 'right', render: (r) => fmt(r.ConvertedTotal, currency) },
          ]}
          rows={data.byCurrency || []}
        />
        <MiniTable
          title="Top GL Accounts"
          columns={[
            { label: 'Account', render: (r) => `${r.AccountCode || ''} ${r.AccountName || ''}` },
            { label: 'Invoices', key: 'InvoiceCount', align: 'right' },
            { label: 'Total', align: 'right', render: (r) => fmt(r.TotalValue, currency) },
          ]}
          rows={data.byGLAccount || []}
          maxRows={15}
        />
      </div>

      {/* ERP Payment Status breakdown — only if data has been synced */}
      {(data.byPaymentStatus || []).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="ERP Payment Status" subtitle="Posted invoices — from Xero / Business Central">
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie
                  data={data.byPaymentStatus || []}
                  dataKey="TotalValue"
                  nameKey="PaymentStatus"
                  cx="50%" cy="50%"
                  outerRadius={65}
                  innerRadius={30}
                  paddingAngle={2}
                  label={({ PaymentStatus, percent }) => `${PaymentStatus} ${(percent * 100).toFixed(0)}%`}
                >
                  {(data.byPaymentStatus || []).map((entry, i) => {
                    const colors = { Paid: '#10b981', PartiallyPaid: '#f59e0b', Open: '#3b82f6', Voided: '#6b7280', 'Not Synced': '#d1d5db' };
                    return <Cell key={i} fill={colors[entry.PaymentStatus] || COLORS[i % COLORS.length]} />;
                  })}
                </Pie>
                <Tooltip formatter={(v) => fmt(v, currency)} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={7} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <MiniTable
            title="Payment Status Detail"
            columns={[
              { label: 'Status', key: 'PaymentStatus' },
              { label: 'Count', key: 'Count', align: 'right' },
              { label: 'Invoice Value', align: 'right', render: (r) => fmt(r.TotalValue, currency) },
              { label: 'Amount Paid', align: 'right', render: (r) => r.TotalPaid > 0 ? fmt(r.TotalPaid, currency) : '—' },
            ]}
            rows={data.byPaymentStatus || []}
          />
        </div>
      )}

      {/* Payment Trend (paid vs unpaid posted invoices by month) */}
      {(data.paymentTrend || []).length > 0 && (
        <ChartCard title="Monthly Payment Progress" subtitle="Posted invoices — paid vs awaiting payment by invoice date">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.paymentTrend || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="Month" tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
              <YAxis tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 10, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={58} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={7} />
              <Bar dataKey="PaidTotal" name="Paid" fill="#10b981" radius={[4,4,0,0]} barSize={14} stackId="a" />
              <Bar dataKey="UnpaidTotal" name="Awaiting Payment" fill="#fbbf24" radius={[4,4,0,0]} barSize={14} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: EXPENSES
// ══════════════════════════════════════════════════════════
function ExpensesTab({ data, currency, fyYear, navigate }) {
  if (!data) return <LoadingSkeleton />;
  const nav = (to, state) => navigate(to, { state: { from: '/finance-dashboard', fromLabel: 'Finance Dashboard', ...(fyYear ? { fyYear } : {}), ...state } });

  return (
    <div className="space-y-4">
      {/* Mileage KPIs */}
      {data.mileage && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <KPICard title="Mileage Claims" value={data.mileage.MileageClaims || 0} icon={TrendingUp} color="cyan" onClick={() => nav('/expenses')} />
          <KPICard title="Total Miles" value={(parseFloat(data.mileage.TotalMiles) || 0).toLocaleString()} icon={TrendingUp} color="blue" onClick={() => nav('/expenses')} />
          <KPICard title="Mileage Value" value={fmt(data.mileage.TotalMileageValue, currency)} icon={DollarSign} color="green" status="green" onClick={() => nav('/expenses')} />
        </div>
      )}

      {/* Status & Category */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Expenses by Status">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={data.byStatus || []}
                dataKey="TotalValue"
                nameKey="Status"
                cx="50%" cy="50%"
                outerRadius={65}
                innerRadius={30}
                paddingAngle={2}
                label={({ Status, percent }) => `${Status} ${(percent * 100).toFixed(0)}%`}
              >
                {(data.byStatus || []).map((entry, i) => (
                  <Cell key={i} fill={STATUS_COLORS[entry.Status] || COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Expenses by Category">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={(data.byCategory || []).slice(0, 10)} layout="vertical" barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 10 }} />
              <YAxis dataKey="Category" type="category" width={80} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Bar dataKey="TotalValue" name="Value" fill="#8b5cf6" radius={[0,5,5,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Department & Top Employees */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Expenses by Department">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={data.byDepartment || []}
                dataKey="TotalValue"
                nameKey="DepartmentName"
                cx="50%" cy="50%"
                outerRadius={65}
                innerRadius={30}
                paddingAngle={2}
                label={({ DepartmentName, percent }) => `${(DepartmentName||'').substring(0,12)} ${(percent * 100).toFixed(0)}%`}
              >
                {(data.byDepartment || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <MiniTable
          title="Top 10 Employees by Expense"
          columns={[
            { label: 'Employee', key: 'EmployeeName' },
            { label: 'Claims', key: 'ExpenseCount', align: 'right' },
            { label: 'Total', align: 'right', render: (r) => fmt(r.TotalValue, currency) },
          ]}
          rows={data.topEmployees || []}
        />
      </div>

      {/* Reimbursement Method */}
      <MiniTable
        title="By Reimbursement Method"
        columns={[
          { label: 'Method', key: 'Method' },
          { label: 'Count', key: 'Count', align: 'right' },
          { label: 'Total', align: 'right', render: (r) => fmt(r.TotalValue, currency) },
        ]}
        rows={data.byReimbursement || []}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: BUDGET & FORECAST
// ══════════════════════════════════════════════════════════
function BudgetsTab({ data, currency, fyYear, navigate }) {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1" /> 
      : <ArrowDown className="w-3 h-3 ml-1" />;
  };

  const sortedDepartments = useMemo(() => {
    if (!data?.byDepartment) return [];
    if (!sortField) return data.byDepartment;
    return [...data.byDepartment].sort((a, b) => {
      let aVal = a[sortField], bVal = b[sortField];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      const numA = parseFloat(aVal), numB = parseFloat(bVal);
      if (!isNaN(numA) && !isNaN(numB)) { aVal = numA; bVal = numB; }
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.byDepartment, sortField, sortDirection]);

  if (!data) return <LoadingSkeleton />;

  return (
    <div className="space-y-4">
      {/* Budget by Department - Bar Chart */}
      <ChartCard title="Budget vs Actual by Department" subtitle="Budgeted, Spent, and Committed amounts">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.byDepartment || []} barCategoryGap="35%" maxBarSize={24}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis dataKey="DepartmentName" tick={{ fontSize: 9 }} angle={-25} textAnchor="end" height={55} />
            <YAxis tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 10 }} />
            <Tooltip formatter={(v) => fmt(v, currency)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="Budgeted" fill="#e2e8f0" name="Budget" radius={[5,5,0,0]} />
            <Bar dataKey="Spent" fill="#3b82f6" name="Spent" radius={[5,5,0,0]} />
            <Bar dataKey="Committed" fill="#f59e0b" name="Committed" radius={[5,5,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {/* Department Table with Utilisation Bars */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100">
          <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Department Budget Utilisation</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Budget</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Spent</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Committed</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Remaining</th>
                <th className="px-3 py-2 text-xs font-medium text-gray-500 uppercase w-40">Utilisation</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.byDepartment || []).map((d, i) => (
                <tr
                  key={i}
                  onClick={() => navigate('/manage-budget', { state: { from: '/finance-dashboard', fromLabel: 'Finance Dashboard' } })}
                  className="hover:bg-blue-50 cursor-pointer transition-colors duration-100 group"
                >
                  <td className="px-3 py-2 font-medium text-gray-900 group-hover:text-blue-700">{d.DepartmentName}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmt(d.Budgeted, currency)}</td>
                  <td className="px-3 py-2 text-right text-blue-600 font-medium">{fmt(d.Spent, currency)}</td>
                  <td className="px-3 py-2 text-right text-amber-600">{fmt(d.Committed, currency)}</td>
                  <td className="px-3 py-2 text-right text-green-600">{fmt(d.Remaining, currency)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <ProgressBar value={parseFloat(d.Spent) + parseFloat(d.Committed)} max={parseFloat(d.Budgeted)} />
                      <span className={`text-xs font-medium ${parseFloat(d.UtilisationPct) >= 90 ? 'text-red-600' : parseFloat(d.UtilisationPct) >= 75 ? 'text-amber-600' : 'text-gray-500'}`}>
                        {pct(d.UtilisationPct)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alerts & GL Accounts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Over-budget Alerts */}
        <div className="bg-white rounded-lg border border-red-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-red-100 bg-red-50/60 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
            <h3 className="text-xs font-semibold text-red-700 uppercase tracking-wide">Budget Alerts (≥80%)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/40">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Department</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">GL Account</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Budget</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Used</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.alerts || []).slice(0, 10).map((a, i) => (
                  <tr key={i} className="hover:bg-red-50/30">
                    <td className="px-4 py-2 text-gray-900">{a.DepartmentName}</td>
                    <td className="px-4 py-2 text-gray-600 text-xs">{a.AccountCode} {a.AccountName}</td>
                    <td className="px-4 py-2 text-right">{fmt(a.Budgeted, currency)}</td>
                    <td className="px-4 py-2 text-right font-medium text-red-600">{fmt(parseFloat(a.Spent) + parseFloat(a.Committed), currency)}</td>
                    <td className="px-4 py-2 text-right font-bold text-red-600">{pct(a.UtilisationPct)}</td>
                  </tr>
                ))}
                {(!data.alerts || data.alerts.length === 0) && (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-green-600">✓ All budgets within limits</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* GL Account Budgets */}
        <MiniTable
          title="Budget by GL Account"
          columns={[
            { label: 'Account', render: (r) => `${r.AccountCode || ''} ${r.AccountName || ''}` },
            { label: 'Budget', align: 'right', render: (r) => fmt(r.Budgeted, currency) },
            { label: 'Spent', align: 'right', render: (r) => fmt(r.Spent, currency) },
            { label: '%', align: 'right', render: (r) => pct(r.UtilisationPct) },
          ]}
          rows={data.byGLAccount || []}
          maxRows={20}
        />
      </div>

      {/* Projects */}
      {data.projects && data.projects.length > 0 && (
        <MiniTable
          title="Active Projects"
          columns={[
            { label: 'Project', render: (r) => `${r.ProjectCode} - ${r.ProjectName}` },
            { label: 'Budget', align: 'right', render: (r) => fmt(r.ProjectBudget, currency) },
            { label: 'Spent', align: 'right', render: (r) => fmt(r.Spent, currency) },
            { label: 'Committed', align: 'right', render: (r) => fmt(r.Committed, currency) },
            { label: 'Remaining', align: 'right', render: (r) => fmt(r.Remaining, currency) },
          ]}
          rows={data.projects}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: VAT & TAX
// ══════════════════════════════════════════════════════════
function VATTab({ data, currency, navigate }) {
  if (!data) return <LoadingSkeleton />;

  return (
    <div className="space-y-4">
      {/* VAT Claims by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="VAT Claims by Status">
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={data.claimsByStatus || []}
                dataKey="VATValue"
                nameKey="Status"
                cx="50%" cy="50%"
                outerRadius={65}
                innerRadius={30}
                paddingAngle={2}
                label={({ Status, percent }) => `${Status} ${(percent * 100).toFixed(0)}%`}
              >
                {(data.claimsByStatus || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Invoice VAT Monthly Trend */}
        <ChartCard title="Invoice VAT by Month">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.invoiceVATMonthly || []} barCategoryGap="35%" maxBarSize={24}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
              <XAxis dataKey="Month" tickFormatter={monthLabel} tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} labelFormatter={monthLabel} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="InvoiceNet" name="Net" fill="#3b82f6" radius={[5,5,0,0]} />
              <Bar dataKey="InvoiceVAT" name="VAT" fill="#10b981" radius={[5,5,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* VAT Claims by Period & VAT by Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniTable
          title="VAT Claims by Return Period"
          columns={[
            { label: 'Period', key: 'Period' },
            { label: 'Claims', key: 'Count', align: 'right' },
            { label: 'VAT Amount', align: 'right', render: (r) => fmt(r.VATValue, currency) },
            { label: 'Gross', align: 'right', render: (r) => fmt(r.GrossValue, currency) },
          ]}
          rows={data.claimsByPeriod || []}
        />

        <MiniTable
          title="Expense VAT by Rate"
          columns={[
            { label: 'VAT Rate', render: (r) => r.VATRate != null ? pct(r.VATRate) : 'N/A' },
            { label: 'Lines', key: 'LineCount', align: 'right' },
            { label: 'VAT Amount', align: 'right', render: (r) => fmt(r.VATValue, currency) },
            { label: 'Net Amount', align: 'right', render: (r) => fmt(r.NetValue, currency) },
          ]}
          rows={data.vatByRate || []}
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// LOADING SKELETON
// ──────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-20 bg-gray-100 rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-64 bg-gray-100 rounded-lg" />
        <div className="h-64 bg-gray-100 rounded-lg" />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: BY VENDOR
// ══════════════════════════════════════════════════════════
function ByVendorTab({ data, currency, fyYear, navigate }) {
  if (!data) return <LoadingSkeleton />;
  const kpis = data.kpis || {};
  const nav = (to, state) => navigate(to, { state: { from: '/finance-dashboard', fromLabel: 'Finance Dashboard', ...(fyYear ? { fyYear } : {}), ...state } });

  // Transform vendor trends into chart-friendly format (one series per vendor)
  const trendMap = {};
  (data.vendorTrends || []).forEach(row => {
    if (!trendMap[row.Month]) trendMap[row.Month] = { Month: row.Month };
    trendMap[row.Month][row.VendorName] = parseFloat(row.MonthlySpend) || 0;
  });
  const trendData = Object.values(trendMap).sort((a, b) => a.Month.localeCompare(b.Month));
  const trendVendors = [...new Set((data.vendorTrends || []).map(r => r.VendorName))];

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard title="Total Vendors" value={(kpis.TotalVendors || 0).toLocaleString()} icon={Briefcase} color="blue" />
        <KPICard title="Total Invoices" value={(kpis.TotalInvoices || 0).toLocaleString()} icon={FileText} color="purple" />
        <KPICard title="Total Spend" value={fmt(kpis.TotalSpend, currency)} icon={DollarSign} color="green" status="green" />
        <KPICard title="Avg Invoice" value={fmt(kpis.AvgInvoiceValue, currency)} icon={Wallet} color="amber" />
      </div>

      {/* Charts: Top vendors stacked bar + Pie concentration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top 15 Vendors by Spend" subtitle="Posted vs Outstanding vs Overdue">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.topVendorsChart || []} layout="vertical" barSize={14}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 11 }} />
              <YAxis dataKey="VendorName" type="category" width={100} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Legend />
              <Bar dataKey="Posted" stackId="a" fill="#3b82f6" name="Posted" />
              <Bar dataKey="Outstanding" stackId="a" fill="#f59e0b" name="Outstanding" />
              <Bar dataKey="Overdue" stackId="a" fill="#ef4444" name="Overdue" radius={[0,5,5,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Vendor Concentration" subtitle="Cumulative % of total spend">
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.concentration || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="VendorName" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={60} />
              <YAxis domain={[0, 100]} tickFormatter={v => v + '%'} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => name === 'CumulativePct' ? v + '%' : fmt(v, currency)} />
              <Area type="monotone" dataKey="CumulativePct" fill="#3b82f680" stroke="#3b82f6" name="Cumulative %" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Monthly trend by top 5 vendors */}
      {trendData.length > 0 && (
        <ChartCard title="Monthly Spend – Top 5 Vendors">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="Month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} labelFormatter={monthLabel} />
              <Legend />
              {trendVendors.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Full vendor table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
          <h3 className="text-sm font-semibold text-gray-700">All Vendors</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Spend</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Invoice</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Outstanding</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Overdue</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">VAT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.vendors || []).map((v, i) => (
                <tr
                  key={v.VendorID || i}
                  onClick={() => nav('/invoices', { vendorFilter: v.VendorID, vendorName: v.VendorName || 'Unknown' })}
                  className="hover:bg-blue-50 cursor-pointer transition-colors duration-100 group"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 group-hover:text-blue-700">{v.VendorName || 'Unknown'}</span>
                      <ExternalLink className="w-3 h-3 text-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">{v.InvoiceCount}</td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900">{fmt(v.TotalSpend, currency)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmt(v.AvgInvoice, currency)}</td>
                  <td className="px-3 py-2 text-right">
                    {parseFloat(v.OutstandingValue) > 0 ? (
                      <span className="text-amber-600 font-medium">{fmt(v.OutstandingValue, currency)} <span className="text-xs">({v.OutstandingCount})</span></span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {parseFloat(v.OverdueValue) > 0 ? (
                      <span className="text-red-600 font-medium">{fmt(v.OverdueValue, currency)} <span className="text-xs">({v.OverdueCount})</span></span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmt(v.TotalVAT, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data.vendors || data.vendors.length === 0) && (
            <div className="text-center py-12 text-gray-500">No vendor data found for this period.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: BY GL ACCOUNT
// ══════════════════════════════════════════════════════════
function ByGLAccountTab({ data, currency, fyYear, navigate }) {
  if (!data) return <LoadingSkeleton />;
  const kpis = data.kpis || {};
  const nav = (to, state) => navigate(to, { state: { from: '/finance-dashboard', fromLabel: 'Finance Dashboard', ...(fyYear ? { fyYear } : {}), ...state } });

  // Transform GL account trends into chart-friendly format
  const trendMap = {};
  (data.nominalTrends || []).forEach(row => {
    if (!trendMap[row.Month]) trendMap[row.Month] = { Month: row.Month };
    trendMap[row.Month][row.GLLabel] = parseFloat(row.MonthlySpend) || 0;
  });
  const trendData = Object.values(trendMap).sort((a, b) => a.Month.localeCompare(b.Month));
  const trendLabels = [...new Set((data.nominalTrends || []).map(r => r.GLLabel))];

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPICard title="GL Accounts" value={(kpis.TotalGLAccounts || 0).toLocaleString()} icon={Layers} color="blue" />
        <KPICard title="Total Invoices" value={(kpis.TotalInvoices || 0).toLocaleString()} icon={FileText} color="purple" />
        <KPICard title="Line Value" value={fmt(kpis.TotalLineValue, currency)} icon={DollarSign} color="green" status="green" />
        <KPICard title="Total Lines" value={(kpis.TotalLines || 0).toLocaleString()} icon={BarChart3} color="amber" />
      </div>

      {/* Charts: Top GL accounts bar + Account type pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Top 15 GL Accounts by Spend">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.topNominalChart || []} layout="vertical" barSize={14}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
              <XAxis type="number" tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 11 }} />
              <YAxis dataKey="GLLabel" type="category" width={120} tick={{ fontSize: 9 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} />
              <Bar dataKey="TotalSpend" name="Spend" fill="#8b5cf6" radius={[0,5,5,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Spend by Account Type">
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={data.byAccountType || []}
                dataKey="TotalSpend"
                nameKey="AccountType"
                cx="50%" cy="50%"
                outerRadius={80}
                innerRadius={40}
                paddingAngle={2}
                label={({ AccountType, percent }) => `${(AccountType || '').substring(0,15)} ${(percent * 100).toFixed(0)}%`}
              >
                {(data.byAccountType || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => fmt(v, currency)} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Monthly trend by top 5 GL accounts */}
      {trendData.length > 0 && (
        <ChartCard title="Monthly Spend – Top 5 GL Accounts">
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="Month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => fmtShort(v, currency)} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmt(v, currency)} labelFormatter={monthLabel} />
              <Legend />
              {trendLabels.map((label, i) => (
                <Line key={label} type="monotone" dataKey={label} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      {/* Full GL account table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
          <h3 className="text-sm font-semibold text-gray-700">All GL Accounts (Invoice Lines)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Account Name</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Invoices</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Lines</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total Spend</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Line</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Vendors</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Depts</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.nominalCodes || []).map((nc, i) => (
                <tr
                  key={nc.GLAccountID || i}
                  onClick={() => nav('/reports', { reportType: 'glAnalysis' })}
                  className="hover:bg-blue-50 cursor-pointer transition-colors duration-100 group"
                >
                  <td className="px-3 py-2 font-mono font-medium text-gray-900 group-hover:text-blue-700">{nc.AccountCode || 'N/A'}</td>
                  <td className="px-3 py-2 text-gray-900 group-hover:text-blue-700">{nc.AccountName || 'Unassigned'}</td>
                  <td className="px-3 py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{nc.AccountType || '—'}</span></td>
                  <td className="px-3 py-2 text-right text-gray-600">{nc.InvoiceCount}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{nc.LineCount}</td>
                  <td className="px-3 py-2 text-right font-medium text-gray-900">{fmt(nc.TotalSpend, currency)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{fmt(nc.AvgLineValue, currency)}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{nc.UniqueVendors}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{nc.UniqueDepartments}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data.nominalCodes || data.nominalCodes.length === 0) && (
            <div className="text-center py-12 text-gray-500">No GL account data found for this period.</div>
          )}
        </div>
      </div>

      {/* GL Account × Department breakdown + Expense by GL account */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniTable
          title="Top GL Accounts × Departments"
          columns={[
            { label: 'Code', key: 'AccountCode' },
            { label: 'Account', key: 'AccountName' },
            { label: 'Department', key: 'DepartmentName' },
            { label: 'Spend', align: 'right', render: (r) => fmt(r.TotalSpend, currency) },
          ]}
          rows={data.codeDeptBreakdown || []}
          maxRows={20}
        />
        <MiniTable
          title="Expenses by GL Account"
          columns={[
            { label: 'Code', key: 'AccountCode' },
            { label: 'Category', key: 'AccountName' },
            { label: 'Lines', key: 'LineCount', align: 'right' },
            { label: 'Total', align: 'right', render: (r) => fmt(r.TotalSpend, currency) },
          ]}
          rows={data.expenseByNominal || []}
          maxRows={20}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// TAB: APPROVER PERFORMANCE
// ══════════════════════════════════════════════════════════
function ApproverPerformanceTab({ data, currency, navigate }) {
  if (!data) return <LoadingSkeleton />;
  const summary = data.summary || {};
  const sla = data.slaCompliance || {};
  const avgHours = parseFloat(summary.OverallAvgResponseHours) || 0;

  const fmtHours = (h) => {
    const hours = parseFloat(h) || 0;
    if (hours < 1) return '<1h';
    if (hours < 24) return hours.toFixed(0) + 'h';
    const days = Math.floor(hours / 24);
    const rem = Math.round(hours % 24);
    return days + 'd ' + rem + 'h';
  };

  const utilizationBar = (hours, max) => {
    const pctVal = max > 0 ? Math.min((hours / max) * 100, 100) : 0;
    const color = hours < 24 ? 'bg-green-500' : hours < 72 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-100 rounded-full h-2 max-w-[100px]">
          <div className={`h-2 rounded-full ${color}`} style={{ width: pctVal + '%' }} />
        </div>
        <span className="text-xs text-gray-500 w-14 text-right">{fmtHours(hours)}</span>
      </div>
    );
  };

  const slaBar = (onTime, total, targetHours) => {
    const pct = total > 0 ? (onTime / total * 100) : 0;
    const color = pct >= 90 ? 'bg-green-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 bg-gray-100 rounded-full h-2 max-w-[100px]">
          <div className={`h-2 rounded-full ${color}`} style={{ width: pct + '%' }} />
        </div>
        <span className="text-xs text-gray-500 w-14 text-right">{pct.toFixed(0)}%</span>
      </div>
    );
  };

  // Find the max hours for bar scaling
  const allHours = [
    ...(data.bestApprovers || []).map(a => a.AvgResponseHours),
    ...(data.slowestApprovers || []).map(a => a.AvgResponseHours),
  ].filter(h => h !== null);
  const maxHours = Math.max(...allHours, 1);

  const allDeptHours = [
    ...(data.bestDepartments || []).map(d => d.AvgResponseHours),
    ...(data.slowestDepartments || []).map(d => d.AvgResponseHours),
  ].filter(h => h !== null);
  const maxDeptHours = Math.max(...allDeptHours, 1);

  // SLA data
  const hasSLA = sla && sla.slas && sla.slas.length > 0;
  const overallSLA = sla.overallCompliance || {};
  const defaultSLA = sla.defaultSLA || {};
  const slaOnTimePct = overallSLA.TotalApprovals > 0
    ? ((overallSLA.OnTimeSLA || 0) / overallSLA.TotalApprovals * 100).toFixed(1)
    : null;

  return (
    <div className="space-y-4">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KPICard title="Completed" value={summary.TotalCompletedApprovals || 0} icon={UserCheck} color="blue" />
        <KPICard title="Avg Response" value={fmtHours(avgHours)} icon={Clock} color={avgHours < 48 ? 'green' : avgHours < 120 ? 'amber' : 'red'} status={avgHours < 48 ? 'green' : avgHours < 120 ? 'amber' : 'red'} />
        <KPICard title="Pending" value={summary.TotalPendingInvoices || 0} icon={AlertTriangle} color="amber" status={(summary.TotalPendingInvoices || 0) > 5 ? 'amber' : 'green'} />
        <KPICard title="Pending Value" value={fmt(summary.PendingTotalValue, currency)} icon={Wallet} color="red" status={parseFloat(summary.PendingTotalValue) > 0 ? 'red' : 'green'} />
        <KPICard title="Oldest Pending" value={summary.OldestPendingDays ? summary.OldestPendingDays + 'd' : '—'} icon={Clock} color={summary.OldestPendingDays > 30 ? 'red' : 'green'} status={summary.OldestPendingDays > 30 ? 'red' : summary.OldestPendingDays > 14 ? 'amber' : 'green'} />
      </div>

      {/* SLA Compliance Section */}
      {hasSLA ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-violet-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-semibold text-purple-800">SLA Compliance</h3>
                <span className="text-xs text-purple-500 ml-2">Target: {defaultSLA.SLAName} ({defaultSLA.TargetHours}h)</span>
              </div>
              {slaOnTimePct !== null && (
                <div className={`text-lg font-bold ${parseFloat(slaOnTimePct) >= 90 ? 'text-green-600' : parseFloat(slaOnTimePct) >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                  {slaOnTimePct}% On-Time
                </div>
              )}
            </div>
          </div>
          <div className="p-4">
            {/* Overall SLA KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-green-50 rounded-md p-2.5 text-center">
                <div className="text-lg font-bold text-green-700">{overallSLA.OnTimeSLA || 0}</div>
                <div className="text-[10px] text-green-600 uppercase">On-Time</div>
              </div>
              <div className="bg-red-50 rounded-md p-2.5 text-center">
                <div className="text-lg font-bold text-red-700">{overallSLA.BreachedSLA || 0}</div>
                <div className="text-[10px] text-red-600 uppercase">Breached</div>
              </div>
              <div className="bg-blue-50 rounded-md p-2.5 text-center">
                <div className="text-lg font-bold text-blue-700">{overallSLA.TotalApprovals || 0}</div>
                <div className="text-[10px] text-blue-600 uppercase">Total</div>
              </div>
              <div className="bg-purple-50 rounded-md p-2.5 text-center">
                <div className="text-lg font-bold text-purple-700">{fmtHours(overallSLA.AvgResponseHours)}</div>
                <div className="text-[10px] text-purple-600 uppercase">Avg Response</div>
              </div>
            </div>

            {/* Per-Approver SLA compliance */}
            {(sla.approverCompliance || []).length > 0 && (
              <div className="mb-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Approver SLA Compliance</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead className="bg-gray-50/40">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approver</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">On-Time</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Breached</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SLA %</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(sla.approverCompliance || []).map((a, i) => {
                        const pct = a.TotalApprovals > 0 ? (a.OnTimeSLA / a.TotalApprovals * 100) : 0;
                        return (
                          <tr key={a.UserID || i} className="hover:bg-purple-50/30">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-900">{a.ApproverName}</div>
                              <div className="text-xs text-gray-500">{a.Email}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-600">{a.DepartmentName || '—'}</td>
                            <td className="px-3 py-2 text-right font-medium">{a.TotalApprovals}</td>
                            <td className="px-3 py-2 text-right text-green-600 font-medium">{a.OnTimeSLA}</td>
                            <td className="px-3 py-2 text-right text-red-600 font-medium">{a.BreachedSLA}</td>
                            <td className="px-3 py-2">{slaBar(a.OnTimeSLA, a.TotalApprovals, defaultSLA.TargetHours)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{fmtHours(a.AvgResponseHours)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{fmt(a.TotalValueProcessed, currency)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Per-Department SLA compliance */}
            {(sla.departmentCompliance || []).length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Department SLA Compliance</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead className="bg-gray-50/40">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approvers</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">On-Time</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Breached</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">SLA %</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(sla.departmentCompliance || []).map((d, i) => (
                        <tr key={d.DepartmentID || i} className="hover:bg-purple-50/30">
                          <td className="px-3 py-2 font-medium text-gray-900">{d.DepartmentName}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{d.ApproverCount}</td>
                          <td className="px-3 py-2 text-right font-medium">{d.TotalApprovals}</td>
                          <td className="px-3 py-2 text-right text-green-600 font-medium">{d.OnTimeSLA}</td>
                          <td className="px-3 py-2 text-right text-red-600 font-medium">{d.BreachedSLA}</td>
                          <td className="px-3 py-2">{slaBar(d.OnTimeSLA, d.TotalApprovals, defaultSLA.TargetHours)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmtHours(d.AvgResponseHours)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{fmt(d.TotalValueProcessed, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 flex items-center gap-3">
          <Shield className="w-5 h-5 text-purple-400" />
          <div>
            <p className="text-sm text-purple-800 font-medium">No SLAs Configured</p>
            <p className="text-xs text-purple-600">
              Go to Finance Configuration &rarr; Approval SLAs to define service level agreements. Once configured, SLA compliance data will appear here.
            </p>
          </div>
        </div>
      )}

      {/* Approver Tables: Best and Slowest side by side */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Best Performing Approvers */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <h3 className="text-sm font-semibold text-green-800">Fastest Approvers</h3>
            </div>
            <p className="text-xs text-green-600 mt-0.5">Ranked by average response time (fastest first)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/40">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approver</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approved</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rejected</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value Processed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.bestApprovers || []).map((a, i) => (
                  <tr key={a.UserID || i} className="hover:bg-green-50/30">
                    <td className="px-3 py-2 text-gray-400 font-medium">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{a.ApproverName || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{a.Email}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{a.DepartmentName || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{a.TotalApprovals}</td>
                    <td className="px-3 py-2 text-right text-green-600">{a.ApprovedCount}</td>
                    <td className="px-3 py-2 text-right text-red-600">{a.RejectedCount}</td>
                    <td className="px-3 py-2">{utilizationBar(a.AvgResponseHours, maxHours)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmt(a.TotalValueProcessed, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data.bestApprovers || data.bestApprovers.length === 0) && (
              <div className="text-center py-10 text-gray-500">No completed approvals found.</div>
            )}
          </div>
        </div>

        {/* Slowest Approvers */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-red-50 to-orange-50">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-600" />
              <h3 className="text-sm font-semibold text-red-800">Slowest Approvers</h3>
            </div>
            <p className="text-xs text-red-600 mt-0.5">Ranked by average response time (slowest first)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/40">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Approver</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approved</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rejected</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value Processed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.slowestApprovers || []).map((a, i) => (
                  <tr key={a.UserID || i} className="hover:bg-red-50/30">
                    <td className="px-3 py-2 text-gray-400 font-medium">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-900">{a.ApproverName || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{a.Email}</div>
                    </td>
                    <td className="px-3 py-2 text-gray-600">{a.DepartmentName || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{a.TotalApprovals}</td>
                    <td className="px-3 py-2 text-right text-green-600">{a.ApprovedCount}</td>
                    <td className="px-3 py-2 text-right text-red-600">{a.RejectedCount}</td>
                    <td className="px-3 py-2">{utilizationBar(a.AvgResponseHours, maxHours)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmt(a.TotalValueProcessed, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data.slowestApprovers || data.slowestApprovers.length === 0) && (
              <div className="text-center py-10 text-gray-500">No completed approvals found.</div>
            )}
          </div>
        </div>
      </div>

      {/* Department Tables: Best and Slowest */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Best Departments */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-blue-800">Fastest Departments</h3>
            </div>
            <p className="text-xs text-blue-600 mt-0.5">Ranked by average approval response time</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/40">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approvers</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approvals</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approved</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rejected</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.bestDepartments || []).map((d, i) => (
                  <tr key={d.DepartmentID || i} className="hover:bg-blue-50/30">
                    <td className="px-3 py-2 text-gray-400 font-medium">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{d.DepartmentName}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{d.ApproverCount}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{d.TotalApprovals}</td>
                    <td className="px-3 py-2 text-right text-green-600">{d.ApprovedCount}</td>
                    <td className="px-3 py-2 text-right text-red-600">{d.RejectedCount}</td>
                    <td className="px-3 py-2">{utilizationBar(d.AvgResponseHours, maxDeptHours)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmt(d.TotalValueProcessed, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data.bestDepartments || data.bestDepartments.length === 0) && (
              <div className="text-center py-10 text-gray-500">No department approval data found.</div>
            )}
          </div>
        </div>

        {/* Slowest Departments */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-orange-600" />
              <h3 className="text-sm font-semibold text-orange-800">Slowest Departments</h3>
            </div>
            <p className="text-xs text-orange-600 mt-0.5">Ranked by average approval response time (slowest first)</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50/40">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approvers</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approvals</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Approved</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Rejected</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Avg Response</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data.slowestDepartments || []).map((d, i) => (
                  <tr key={d.DepartmentID || i} className="hover:bg-orange-50/30">
                    <td className="px-3 py-2 text-gray-400 font-medium">{i + 1}</td>
                    <td className="px-3 py-2 font-medium text-gray-900">{d.DepartmentName}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{d.ApproverCount}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{d.TotalApprovals}</td>
                    <td className="px-3 py-2 text-right text-green-600">{d.ApprovedCount}</td>
                    <td className="px-3 py-2 text-right text-red-600">{d.RejectedCount}</td>
                    <td className="px-3 py-2">{utilizationBar(d.AvgResponseHours, maxDeptHours)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{fmt(d.TotalValueProcessed, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!data.slowestDepartments || data.slowestDepartments.length === 0) && (
              <div className="text-center py-10 text-gray-500">No department approval data found.</div>
            )}
          </div>
        </div>
      </div>

      {/* Longest Unapproved Invoices */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-yellow-50">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-800">Longest Unapproved Invoices</h3>
          </div>
          <p className="text-xs text-amber-600 mt-0.5">Invoices sitting in Draft or Pending status the longest — oldest first</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Department</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice Date</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Days in System</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Current Approver</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Created By</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {(data.longestPending || []).map((inv, i) => {
                const daysColor = inv.DaysInSystem > 60 ? 'text-red-600 font-bold' : inv.DaysInSystem > 30 ? 'text-amber-600 font-semibold' : 'text-gray-600';
                return (
                  <tr key={inv.InvoiceID || i} className="hover:bg-amber-50/30">
                    <td className="px-3 py-2 font-mono font-medium text-gray-900">{inv.InvoiceNumber || '—'}</td>
                    <td className="px-3 py-2 text-gray-900">{inv.VendorName || '—'}</td>
                    <td className="px-3 py-2 text-gray-600">{inv.DepartmentName || '—'}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900">{fmt(inv.TotalAmount, inv.Currency || currency)}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        inv.Status === 'Pending' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
                      }`}>{inv.Status}</span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{inv.InvoiceDate ? new Date(inv.InvoiceDate).toLocaleDateString('en-GB') : '—'}</td>
                    <td className={`px-3 py-2 text-right ${daysColor}`}>
                      {inv.DaysInSystem} days
                    </td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{inv.CurrentApproverName || '—'}</td>
                    <td className="px-3 py-2 text-gray-600 text-xs">{inv.CreatedByName || '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <a href={`/all-documents?search=${encodeURIComponent(inv.InvoiceNumber || inv.InvoiceID)}`} className="text-blue-600 hover:text-blue-800">
                        <ExternalLink className="w-4 h-4 inline" />
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(!data.longestPending || data.longestPending.length === 0) && (
            <div className="text-center py-10 text-gray-500">No pending invoices found — all caught up!</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// MAIN DASHBOARD PAGE
// ══════════════════════════════════════════════════════════
const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'invoices', label: 'Invoices & Payables', icon: FileText },
  { id: 'byVendor', label: 'By Vendor', icon: Briefcase },
  { id: 'byNominal', label: 'By GL Account', icon: Layers },
  { id: 'expenses', label: 'Expenses', icon: Receipt },
  { id: 'budgets', label: 'Budget & Forecast', icon: PiggyBank },
  { id: 'vat', label: 'VAT & Tax', icon: DollarSign },
  { id: 'approverPerformance', label: 'Approver Performance', icon: UserCheck },
];

export default function FinanceDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filter state
  const [filters, setFilters] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const [selectedFY, setSelectedFY] = useState('');
  const [selectedDept, setSelectedDept] = useState('');

  // Data state
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [vatData, setVatData] = useState(null);
  const [vendorData, setVendorData] = useState(null);
  const [nominalData, setNominalData] = useState(null);
  const [approverData, setApproverData] = useState(null);
  const [currency, setCurrency] = useState('GBP');

  const filterParams = useMemo(() => {
    const p = {};
    if (selectedFY) p.fyId = selectedFY;
    if (selectedDept) p.departmentId = selectedDept;
    return p;
  }, [selectedFY, selectedDept]);

  // Resolve selected FY to a start year number for passing to invoice list
  const fyStartYear = useMemo(() => {
    if (!selectedFY || !filters?.financialYears) return null;
    const fy = filters.financialYears.find(f => String(f.FinancialYearID) === String(selectedFY));
    return fy?.StartDate ? new Date(fy.StartDate).getFullYear() : null;
  }, [filters, selectedFY]);

  // Load filter options on mount
  useEffect(() => {
    loadFilters();
  }, []);

  // Load tab data when tab or filters change — but only after filters have been initialised
  useEffect(() => {
    if (!filtersReady) return;
    loadTabData(activeTab);
  }, [activeTab, filterParams, filtersReady]);

  const loadFilters = async () => {
    try {
      const res = await financeDashboardAPI.getFilters();
      setFilters(res.data);
      // Auto-select current FY
      const currentFY = (res.data.financialYears || []).find(fy => fy.IsCurrent);
      if (currentFY) setSelectedFY(currentFY.FinancialYearID);
      setFiltersReady(true);
    } catch (err) {
      console.error('Failed to load filters:', err);
    }
  };

  const loadTabData = useCallback(async (tab) => {
    setLoading(true);
    try {
      switch (tab) {
        case 'overview': {
          const [ovr, trd] = await Promise.all([
            financeDashboardAPI.getOverview(filterParams),
            financeDashboardAPI.getTrends(filterParams),
          ]);
          setOverview(ovr.data);
          setTrends(trd.data.trends || []);
          setCurrency(ovr.data.defaultCurrency || 'GBP');
          break;
        }
        case 'invoices': {
          const res = await financeDashboardAPI.getInvoices(filterParams);
          setInvoiceData(res.data);
          break;
        }
        case 'expenses': {
          const res = await financeDashboardAPI.getExpenses(filterParams);
          setExpenseData(res.data);
          break;
        }
        case 'budgets': {
          const res = await financeDashboardAPI.getBudgets(filterParams);
          setBudgetData(res.data);
          break;
        }
        case 'vat': {
          const res = await financeDashboardAPI.getVat(filterParams);
          setVatData(res.data);
          break;
        }
        case 'byVendor': {
          const res = await financeDashboardAPI.getByVendor(filterParams);
          setVendorData(res.data);
          break;
        }
        case 'byNominal': {
          const res = await financeDashboardAPI.getByNominalCode(filterParams);
          setNominalData(res.data);
          break;
        }
        case 'approverPerformance': {
          const [perfRes, slaRes] = await Promise.all([
            financeDashboardAPI.getApproverPerformance(filterParams),
            approvalSLAsAPI.getCompliance(filterParams).catch(() => ({ data: null }))
          ]);
          setApproverData({ ...perfRes.data, slaCompliance: slaRes.data });
          break;
        }
      }
    } catch (err) {
      console.error(`Failed to load ${tab}:`, err);
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadTabData(activeTab);
    setRefreshing(false);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Finance Dashboard</h1>
            <p className="text-xs text-gray-500">Analytics overview</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-gray-200 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Filter className="w-3.5 h-3.5" />
              <span className="font-medium">Filters</span>
            </div>

            {/* Financial Year */}
            <div className="relative">
              <select
                value={selectedFY}
                onChange={(e) => setSelectedFY(e.target.value)}
                className="appearance-none bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1 pr-7 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Financial Years</option>
                {(filters?.financialYears || []).map(fy => (
                  <option key={fy.FinancialYearID} value={fy.FinancialYearID}>
                    {fy.YearLabel} {fy.IsCurrent ? '(Current)' : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Department */}
            <div className="relative">
              <select
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
                className="appearance-none bg-gray-50 border border-gray-200 rounded-md px-2.5 py-1 pr-7 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Departments</option>
                {(filters?.departments || []).map(d => (
                  <option key={d.DepartmentID} value={d.DepartmentID}>{d.Name}</option>
                ))}
              </select>
              <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            {/* Clear Filters */}
            {(selectedFY || selectedDept) && (
              <button
                onClick={() => { setSelectedFY(''); setSelectedDept(''); }}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-1 overflow-x-auto -mb-px" aria-label="Tabs">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="min-h-[400px]">
          {loading && !overview && !invoiceData && !expenseData && !budgetData && !vatData && !vendorData && !nominalData && !approverData ? (
            <LoadingSkeleton />
          ) : (
            <>
              {activeTab === 'overview' && <OverviewTab overview={overview} trends={trends} currency={currency} filters={filters} selectedFY={selectedFY} fyYear={fyStartYear} navigate={navigate} />}
              {activeTab === 'invoices' && <InvoicesTab data={invoiceData} currency={currency} fyYear={fyStartYear} navigate={navigate} />}
              {activeTab === 'byVendor' && <ByVendorTab data={vendorData} currency={currency} fyYear={fyStartYear} navigate={navigate} />}
              {activeTab === 'byNominal' && <ByGLAccountTab data={nominalData} currency={currency} fyYear={fyStartYear} navigate={navigate} />}
              {activeTab === 'expenses' && <ExpensesTab data={expenseData} currency={currency} fyYear={fyStartYear} navigate={navigate} />}
              {activeTab === 'budgets' && <BudgetsTab data={budgetData} currency={currency} fyYear={fyStartYear} navigate={navigate} />}
              {activeTab === 'vat' && <VATTab data={vatData} currency={currency} navigate={navigate} />}
              {activeTab === 'approverPerformance' && <ApproverPerformanceTab data={approverData} currency={currency} navigate={navigate} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
